/**
 * Which roles started when they were planned to, and which did not.
 *
 * ⚠️ TWO POPULATIONS IN ONE LIST, and each row says which it is. A role somebody has STARTED has a
 * final slip — it was this many months late and will not change. A role still OPEN past its month
 * has a slip that is still growing, measured to today. Leaving the open ones out would flatter the
 * figure exactly when the trimester is going badly, which is when it gets read.
 *
 * ⚠️ MONTHS, NOT WEEKS. The Hiring Database stores a planned start MONTH, so weeks would be
 * arithmetic on precision the source does not have. Theresa asked for "weeks / months"; months is
 * the honest half of that, and the rule says so rather than dividing by seven to look precise.
 */
import type { RoleSlip, StartAccuracy } from '../../../utils/deliveryTiles';
import { Section } from './trackingUi';

const BUCKETS: { key: keyof Pick<StartAccuracy, 'wayOff' | 'slipped' | 'onTime'>; title: string; note: string }[] = [
  { key: 'wayOff', title: 'Three months or more out', note: 'these are a different conversation from a slip' },
  { key: 'slipped', title: 'One or two months late', note: 'the ordinary slip' },
  { key: 'onTime', title: 'Started on time or early', note: '' },
];

export function StartAccuracySection({
  accuracy,
  hasBaseline,
}: {
  accuracy: StartAccuracy;
  /** Whether a plan was ever frozen. ⚠️ Not the same fact as "no role has a start yet". */
  hasBaseline: boolean;
}) {
  if (!accuracy.measured) {
    return (
      <Section title="Start date accuracy">
        <p className="mt-2 text-sm text-gray-500">
          {hasBaseline
            ? 'No role in this window has both a planned month and a start to compare it against yet.'
            : 'Nothing has been frozen as this trimester’s plan, so there is no planned month to measure a start against. Freeze a baseline on Planning → TA capacity planning.'}
        </p>
      </Section>
    );
  }

  return (
    <Section
      title="Start date accuracy"
      aside={`${accuracy.measured} roles measured · in months, which is what the plan stores`}
    >
    <div className="mt-3 space-y-3">
      {BUCKETS.map(({ key, title, note }) => {
        const rows = accuracy[key];
        if (!rows.length) return null;
        return (
          <div
            key={key}
            data-testid={`starts-${key}`}
            className="overflow-hidden rounded-xl border border-gray-200 bg-white"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 border-b border-gray-100 px-5 py-2.5">
              <h3 className="text-sm font-semibold text-gray-900">
                {title} <span className="ml-1 font-normal text-gray-500">({rows.length})</span>
              </h3>
              {note ? <span className="text-xs text-gray-500">{note}</span> : null}
            </div>
            <ul className="divide-y divide-gray-100">
              {rows.map((r) => (
                <Row key={r.positionId || r.title} role={r} />
              ))}
            </ul>
          </div>
        );
      })}

      <p className="text-xs leading-relaxed text-gray-500">
        Measured in months, because the Hiring Database stores a planned start <b>month</b>. A role
        that has started carries its final slip; one still open past its month is measured to today
        and is <b>still growing</b> — that is what &ldquo;still open&rdquo; on a row means.
      </p>
    </div>
    </Section>
  );
}

function Row({ role }: { role: RoleSlip }) {
  const late = role.slipMonths > 0;
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-5 py-2 text-sm">
      <span className="text-gray-900">
        {role.title}
        {role.recruiter ? <span className="ml-2 text-xs text-gray-500">{role.recruiter}</span> : null}
      </span>
      <span className="tabular-nums text-gray-500">
        planned {role.plannedMonth}
        {role.actualMonth ? ` · started ${role.actualMonth}` : ' · still open'} ·{' '}
        <span className={late ? 'font-medium text-amber-700' : 'font-medium text-emerald-700'}>
          {late ? `+${role.slipMonths} ${role.slipMonths === 1 ? 'month' : 'months'}` : 'on time'}
        </span>
      </span>
    </li>
  );
}
